import { useCallback, useEffect, useState } from 'react';
import {
  CHAT_AI_ENABLE_CHAT_SERVER_CHANGED,
  fetchProjectSettingsForEnableChat,
} from '../api/gatewayProjectEnableChat';

/**
 * Loads `enableChat` flags from GET /project/settings (normalized) and refreshes on save events.
 *
 * @param {string|null|undefined} projectId
 * @returns {{ serverModuleMap: Record<string, boolean>, loading: boolean, error: string|null, refetch: () => Promise<void> }}
 */
export function useModuleEnableChatServerMap(projectId) {
  const [serverModuleMap, setServerModuleMap] = useState({});
  const [loading, setLoading] = useState(Boolean(projectId));
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    if (!projectId) {
      setServerModuleMap({});
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const r = await fetchProjectSettingsForEnableChat(projectId);
      setServerModuleMap(r?.moduleEnableChatMap ?? {});
    } catch (e) {
      setServerModuleMap({});
      setError(e?.message || 'Could not load module chat settings.');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    const onEvent = (e) => {
      if (e.detail?.projectId === projectId && e.detail?.moduleEnableChatMap) {
        // Replace: save events carry a full map from merged moduleInfo; merging
        // into stale GET state would leave old false entries for unchanged keys.
        setServerModuleMap(e.detail.moduleEnableChatMap);
      }
    };
    window.addEventListener(CHAT_AI_ENABLE_CHAT_SERVER_CHANGED, onEvent);
    return () =>
      window.removeEventListener(CHAT_AI_ENABLE_CHAT_SERVER_CHANGED, onEvent);
  }, [projectId]);

  return { serverModuleMap, loading, error, refetch };
}
